import { Divider, FormControlLabel, MenuItem, Stack, Switch, Typography } from "@mui/material";
import {
  Authorizer,
  AutocompleteStructurePartners,
  CheckboxForm,
  EditorForm,
  ModalFormFieldContact,
  StackGrid,
  TextFieldForm,
  useAuthorizer,
} from "@styleguide";
import type { FormEventHandler, MutableRefObject } from "react";
import { type Control, Controller, type UseFormReturn, useWatch } from "react-hook-form";
import type { IDocumentsActionsProgress } from "../logics/documents";
import type { TModalSocialOfferFormResolver } from "../logics/resolver";
import { ModalSocialOfferFormFieldAddress } from "./address";
import { ModalSocialOfferFormFieldChallenges } from "./challenges";
import { ModalSocialOfferFormFieldDocuments } from "./documents";
import { ModalSocialOfferFormFieldFinancingModes } from "./financingModes";
import { ModalSocialOfferFormFieldPerimeter } from "./perimeter";
import { ModalSocialOfferFormFieldPeriod } from "./period";
import { ModalSocialOfferFormFieldTags } from "./tags";

const OldStructureFields = ({ control }: { control: Control<TModalSocialOfferFormResolver> }) => (
  <>
    <StackGrid columnGap={2} perLine={2}>
      <TextFieldForm control={control} name="provider.name" label="Nom de la structure *" fullWidth size="small" />
    </StackGrid>
    <TextFieldForm control={control} name="provider.website" label="Site internet" fullWidth size="small" />
  </>
);

interface IModalSocialOfferFormProps {
  hookForm: UseFormReturn<TModalSocialOfferFormResolver>;
  onSubmit: FormEventHandler;
  formRef?: MutableRefObject<HTMLFormElement | null>;
  loading: boolean;
  documentsProgress: IDocumentsActionsProgress | null;
}

export const ModalSocialOfferForm = ({
  hookForm: { control, setValue },
  onSubmit,
  formRef,
  loading,
  documentsProgress,
}: IModalSocialOfferFormProps) => {
  const providerName = useWatch({ control, name: "provider.name" });
  const providerId = useWatch({ control, name: "provider.providerId" });

  const isAuthorizedTags = useAuthorizer(["offers.social.besoins.v2"]);

  return (
    <form ref={formRef} onSubmit={onSubmit}>
      <Stack
        data-testid="social-offer-create-form"
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="body1SemiBold">À propos</Typography>
        <TextFieldForm control={control} name="name" label="Titre de l'offre *" fullWidth size="small" />
        <StackGrid rowGap={2} columnGap={2} perLine={2}>
          <TextFieldForm control={control} name="permanent" label="Type d'offre *" select size="small">
            <MenuItem value="permanent">Permanente</MenuItem>
            <MenuItem value="temporary">Temporaire</MenuItem>
          </TextFieldForm>
          <ModalSocialOfferFormFieldPeriod control={control} />
        </StackGrid>
        <StackGrid rowGap={2} columnGap={2} perLine={2}>
          {isAuthorizedTags ? (
            <ModalSocialOfferFormFieldTags control={control} />
          ) : (
            <ModalSocialOfferFormFieldChallenges control={control} />
          )}
          <ModalSocialOfferFormFieldFinancingModes control={control} />
        </StackGrid>
        <Divider />
        <Typography variant="body1SemiBold">Présentation du service</Typography>
        <EditorForm
          id="social-offer-form-description"
          control={control}
          name="description"
          width="100%"
          height={418}
          maxHeight={280}
        />
        <Divider />
        <Typography variant="body1SemiBold">Qui peut bénéficier de ce service ?</Typography>
        <EditorForm
          id="social-offer-form-beneficiaries"
          control={control}
          name="beneficiaries"
          width="100%"
          height={184}
          maxHeight={184}
        />
        <ModalSocialOfferFormFieldPerimeter control={control} />
        <Divider />
        <Typography variant="body1SemiBold">Comment s&apos;inscrire ?</Typography>
        <EditorForm
          id="social-offer-form-registrationModalities"
          control={control}
          name="registrationModalities"
          width="100%"
          height={184}
          maxHeight={184}
        />
        <TextFieldForm control={control} name="url" label="URL de l'offre" fullWidth size="small" />
        <Divider />
        <Typography variant="body1SemiBold">Information utiles</Typography>
        <Authorizer
          requiredPermissions={["partner.dashboard.visualize"]}
          Forbidden={OldStructureFields}
          ForbiddenProps={{ control }}
        >
          <Controller
            control={control}
            name="provider.providerId"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AutocompleteStructurePartners
                value={value ? { _id: value, name: providerName } : providerName ? { name: providerName } : null}
                size="small"
                onChange={(_, value) => {
                  onChange(value?._id ?? "");
                  setValue("provider.name", value?.name ?? "");
                  setValue("createStructurePartner", true);
                }}
                TextFieldProps={{
                  label: "Structure partenaire *",
                  error: Boolean(error),
                  helperText: error?.message,
                }}
              />
            )}
          />
          {providerName && !providerId && (
            <Controller
              name="createStructurePartner"
              control={control}
              render={({ field: { value, onChange, onBlur, name, ref } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      name={name}
                      size="small"
                      slotProps={{ input: { ref } }}
                    />
                  }
                  label={
                    <Typography variant="body1">
                      Voulez-vous ajouter ce partenaire à la base de donnée Horizon ? Une nouvelle page partenaire sera
                      créée après l’ajout de cette offre.
                    </Typography>
                  }
                  sx={{ alignItems: "flex-start", ml: 0 }}
                />
              )}
            />
          )}
        </Authorizer>
        <CheckboxForm
          name="isRemote"
          control={control}
          label="L’offre est disponible à distance"
          size="small"
          sx={{ my: -1 }}
        />
        <ModalSocialOfferFormFieldAddress control={control} />
        <Controller
          name="contact"
          control={control}
          render={({ field: { value, onChange }, formState: { errors } }) => (
            <ModalFormFieldContact value={value} onChange={onChange} errors={errors} disabled={!providerName} />
          )}
        />
        <Divider />
        <ModalSocialOfferFormFieldDocuments control={control} loading={loading} documentsProgress={documentsProgress} />
      </Stack>
    </form>
  );
};
